/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Pribyl (pribyl@cadwork.cz)
 *
 */

import AnimatedObject from "App/animations/animated_object";
import {vec3} from "gl-matrix"
import Interpolator from "App/animations/interpolators/interpolator";
import IScene from "js_interface/src/scene_interface";

export default
class CameraAnimator {
    private static animatorID: number = 0;
    private _sceneAPI: IScene;
    private _ID: number;

    private _animatedObject: any = null;
    private _animationProgressCB: (aState: number, aSpeed: number) => void = null;
    private _animationFinishCB: () => void = null;

    private _frames: any[] = [];
    private _animationFactor: number = 0;
    private _timeFromStart: number = 0;
    private _interpolator: Interpolator = null;
    private _paused: boolean = false;

    private _deviceInputEnabled: boolean = false;

    private _startTimeStamp: number;
    private _currentTimeStamp: number;
    private _endTimeStamp: number;
    private _fps: number;
    private _endless: boolean;
    private _autoDestruction: boolean;
    private _pauseTimeStamp: number;
    private _returnTimeStamp: number;

    private _preventTexturesSwitching: boolean;

    private _frameTime: number;
    private _animationTime: number;
    private _animationDuration: number;

    constructor(aSceneAPI: IScene) {
        this._sceneAPI = aSceneAPI;
        this._ID = CameraAnimator.animatorID++;

        this._init();
    }

    public getID(): number {
        return this._ID;
    }

    public setAnimationFrames(aFrames) {
        this._frames = aFrames.slice();

        this._updateAnimationDuration();

        if (this._interpolator !== null) {
            this._interpolator.init(this._frames[0]);
        }
    }

    public setFps(aFps: number): void {
        this._fps = aFps;

        this._updateAnimationDuration();
    }

    public setEndless(aValue: boolean): void {
        this._endless = aValue;
    }

    public setAutoDestruction(aValue: boolean): void {
        this._autoDestruction = aValue;
    }

    public setInterpolator(aInterpolator): void {
        this._interpolator = aInterpolator;

        if (this._frames.length > 0) {
            this._interpolator.init(this._frames[0]);
        }
    }

    public getInterpolator(): Interpolator {
        return this._interpolator;
    }

    public attachObject(aAnimatedObject: AnimatedObject): void {
        this._animatedObject = aAnimatedObject;
    }

    public preventTexturesSwitching(aValue: boolean): void {
        this._preventTexturesSwitching = aValue;
    }

    public startAnimation(): void {
        if (this._preventTexturesSwitching) {
            this._sceneAPI.renderSettingsModule.setPauseTexturesSwitching(true);
        }
    }

    public getAnimationFinished(): boolean {
        if (!this._paused) {
            if (this._endTimeStamp - this._currentTimeStamp <= 0) {
                if (this._endless) { // handle endless animations for e.g. y rotation or manipulator translations etc.
                    this.jumpTo(0);
                } else if (!this._autoDestruction) {
                    if (this._animationFinishCB !== undefined && this._animationFinishCB !== null) {
                        this._animationFinishCB();
                    }
                    this.pause();
                    this.jumpTo(0);
                } else {
                    return true;
                }
            }
        } else {
            return false;
        }
    }

    public finishAnimation(): void {
        let finished = this.getAnimationFinished();

        if (finished && this._animationFinishCB !== undefined && this._animationFinishCB !== null) {
            this._animationFinishCB();
        }

        if (finished) {
            if (this._preventTexturesSwitching) {
                this._sceneAPI.renderSettingsModule.setPauseTexturesSwitching(false);
                this._sceneAPI.viewModeModule.notifyViewPointChanged();
            }
        }
    }

    public getAnimatedObject(): any {
        return this._animatedObject;
    }

    public setAnimationProgressCB(anAnimationProgressCB: (aState: number, aSpeed: number) => void): void {
        this._animationProgressCB = anAnimationProgressCB;
    }

    public setAnimationFinishCB(anAnimationFinishCB: () => void): void {
        this._animationFinishCB = anAnimationFinishCB;
    }

    public getTime(): number {
        return this._pauseTimeStamp + (new Date().getTime() - this._returnTimeStamp);
    }

    public run(): void {
        if (this._paused) {
            this._paused = false;
            this._returnTimeStamp = new Date().getTime();

            if (this._preventTexturesSwitching) {
                this._sceneAPI.renderSettingsModule.setPauseTexturesSwitching(true);
            }
        }
    }

    public pause(): void {
        if (!this._paused) {
            this._paused = true;
            this._pauseTimeStamp = this._currentTimeStamp;

            if (this._preventTexturesSwitching) {
                this._sceneAPI.renderSettingsModule.setPauseTexturesSwitching(false);
            }
        }
    }

    public stop(): void {
        if (this._preventTexturesSwitching) {
            this._sceneAPI.renderSettingsModule.setPauseTexturesSwitching(false);
        }
    }

    public jumpTo(aPercentage): void {
        if (aPercentage >= 0 && aPercentage <= 1.0) {
            this._animationFactor = aPercentage;
            this._timeFromStart = aPercentage * this._animationDuration;
            this._startTimeStamp = this._currentTimeStamp - this._timeFromStart;
            this._endTimeStamp = this._startTimeStamp + this._animationDuration;

            this._computeInterpolation();
        }
    }

    public setRepeat(aValue): void {
        this._endless = aValue;
    }

    public animationTick(): boolean {
        if (this._paused) {
            return false;
        }

        let prevTimeStamp = this._currentTimeStamp;
        let prevInterPosition = this._interpolator.getPosition();

        this._currentTimeStamp = this.getTime();

        if (this._startTimeStamp === 0) {
            this._startTimeStamp = this._currentTimeStamp;
            this._endTimeStamp = this._startTimeStamp + this._animationDuration;
        }

        this._timeFromStart = this._currentTimeStamp - this._startTimeStamp;
        this._animationFactor = this._timeFromStart / this._animationDuration;

        this._computeInterpolation();

        let dT = (this._currentTimeStamp - prevTimeStamp) / 1000;
        let dS = vec3.distance(this._interpolator.getPosition(), prevInterPosition);

        let v = dS / dT * 3.6; // km/h

        if (this._animationFactor > 1.0) {
            this._animationFactor = 1.0;
            v = 0;
        }

        this._animatedObject.animated(this, this._animationFactor);

        // request render of the scene
        this._sceneAPI.viewModeModule.scheduleRedraw();

        if (this._animationProgressCB !== undefined && this._animationProgressCB !== null) {
            this._animationProgressCB(this._animationFactor * 100, v);
        }
    };

    public setEnableDeviceInput(aValue: boolean): void {
        this._deviceInputEnabled = aValue;
    }

    public getDeviceInputEnabled(): boolean {
        return this._deviceInputEnabled;
    }

    public getCameraPosition(): vec3 {
        return this._interpolator.getPosition();
    }

    public getCameraTarget(): vec3 {
        return this._interpolator.getTarget();
    }

    private _init(): void {
        this._startTimeStamp = 0;
        this._currentTimeStamp = 0;
        this._endTimeStamp = 0;

        this._fps = 1;
        this._endless = false;
        this._autoDestruction = true;

        this._paused = false;
        this._pauseTimeStamp = 0;
        this._returnTimeStamp = 0;

        this._preventTexturesSwitching = false;
    }

    private _updateAnimationDuration(): void {
        this._frameTime = (1.0 / this._fps) * 1000;
        this._animationTime = (this._frames.length - 1) * this._frameTime;
        this._animationDuration = this._animationTime;
    }

    private _computeCurrentFrame(aTimeFromStart: number): number {
        let frame = Math.floor(aTimeFromStart / (this._frameTime));

        if (frame >= (this._frames.length - 1)) {
            return this._frames.length - 2;
        }

        return frame;
    }

    private _computeInterpolation(): {position: vec3, target: vec3} {
        let frame = this._computeCurrentFrame(this._timeFromStart);

        let currentFrameBeginTime = frame * this._frameTime;
        let currentTimeWithinFrame = this._timeFromStart - currentFrameBeginTime;
        let frameTimeFactor = currentTimeWithinFrame / this._frameTime;

        // this can happen when the animation tick is longer then frame time
        if (frameTimeFactor > 1.0) {
            frameTimeFactor = 1.0;
        }

        return this._interpolator.interpolate(this._frames[frame], this._frames[frame + 1], frameTimeFactor);
    }
}
